import { useDispatch } from "react-redux";
import {
  GetTradesRequest,
  SocketTopics,
  SocketTradeData,
  TradeStatus,
} from "@shared/types";
import { SocketArgs } from "@shared/services/socket/types";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  updateClosedTradesData,
  updateOpenTradesData,
} from "@shared/features/trades/common/utils";
import { ClosedTradeStatus } from "@shared/features/trades/common/constants";
import useSocket from "@shared/features/socket/useSocket";
import { useEffect } from "react";
import { socketsSlices } from "@shared/slices/sockets.slice";

const useSocketAggTradeData = (
  socketArgs: SocketArgs<SocketTopics.AGG_TRADES> | typeof skipToken,
  getOpenTradesArgs?: GetTradesRequest | undefined | typeof skipToken,
  getClosedTradesArgs?: GetTradesRequest | undefined | typeof skipToken,
  delayUpdateOnClose?: number,
  refetchOpenTrades?: () => void
) => {
  const dispatch = useDispatch();

  const socketData = useSocket<SocketTopics.AGG_TRADES>(
    SocketTopics.AGG_TRADES,
    socketArgs
  );

  const dispatchUpdateTrades = (trades: SocketTradeData[]) => {
    if (getOpenTradesArgs !== skipToken) {
      dispatch(
        updateOpenTradesData(getOpenTradesArgs, trades, dispatch) as any
      );
    }
    if (getClosedTradesArgs !== skipToken) {
      dispatch(
        updateClosedTradesData(getClosedTradesArgs, trades, dispatch) as any
      );
    }
  };

  useEffect(() => {
    const socketTradeData = socketData?.value;
    if (socketTradeData) {
      dispatch(
        socketsSlices[SocketTopics.AGG_TRADES].actions.updateSocketData(
          undefined
        )
      );
      if ("fetch_initial_data" in socketTradeData) {
        try {
          refetchOpenTrades?.();
        } catch {}
      } else {
        const immediateUpdate: SocketTradeData[] = [];
        const delayedUpdate: SocketTradeData[] = [];
        socketTradeData.forEach((trade) => {
          const tradeStatus = trade?.status_value;
          if (
            delayUpdateOnClose &&
            tradeStatus &&
            ClosedTradeStatus.has(tradeStatus) &&
            tradeStatus !== TradeStatus.EDITED
          ) {
            delayedUpdate.push(trade);
          } else {
            immediateUpdate.push(trade);
          }
        });
        if (delayedUpdate.length) {
          setTimeout(
            () => dispatchUpdateTrades(delayedUpdate),
            delayUpdateOnClose
          );
        }
        dispatchUpdateTrades(immediateUpdate);
      }
    }
  }, [socketData]);

  return socketData?.value;
};

export default useSocketAggTradeData;
